import { addDays, differenceInMilliseconds, format, startOfISOWeek, subWeeks } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

import { BONUS_PUB_ACUMULATOR } from "./constants";
import { isThursdayNight } from "./thursday-time";

/**
 * The pub coin economy: how much a night out earns on top of the hourly rate,
 * and what you're fined for. Every number that decides how fast coin flows in
 * or out lives here so the whole economy can be tuned from one place; see
 * economy-sim.ts for the year-long scenarios these values were balanced
 * against, and economy-sim.test.ts for the targets they must keep meeting.
 *
 * Everything here is pure and environment-agnostic: the backend uses it to
 * credit and fine, and the frontend uses the same functions to show what a
 * live session has earned so far.
 */

const TIMEZONE = "Europe/London";
const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;

/**
 * Coins per hour with no streak at an ordinary pub, and all any check-in that
 * isn't a Thursday one ever earns. Deliberately a pittance: the coin is in
 * the Thursday bonuses and in building a streak.
 */
export const BASE_COINS_PER_HOUR = 1;

/**
 * How far back a streak is counted, in consecutive weeks of attendance.
 */
export const MAX_STREAK_WEEKS = 52;

/**
 * The streak multiplier grows by STREAK_MULTIPLIER_PER_WEEK for every week of
 * the streak, up to MAX_STREAK_MULTIPLIER. It grows linearly, so every week
 * of a streak is worth the same until it tops out: a single week already
 * multiplies the base rate several times over, and it tops out after about
 * half a year.
 */
export const STREAK_MULTIPLIER_PER_WEEK = 4;
export const MAX_STREAK_MULTIPLIER = 100;

/**
 * What the daily super bonus pub multiplies the Thursday rate by; the daily
 * bonus pub (and any venue marked `bonus`) pays BONUS_PUB_ACUMULATOR. Both
 * are rolled in functions/src/interfaces/venues.ts.
 */
export const MAX_VENUE_MULTIPLIER = 10;
export const SUPER_BONUS_PUB_ID = "super-bonus-pub";
export const BONUS_PUB_ID = "bonus-pub";

/** A "night" runs from 6am to 6am London time, matching when sessions are closed. */
export const NIGHT_ROLLOVER_HOUR = 6;

export interface CoinLineItem {
  id: string;
  label: string;
  coin: number;
}

/**
 * What counts as properly staying out: at least STICK_AROUND_HOURS checked in
 * within a STICK_AROUND_WINDOW_HOURS stretch (the slack is for walking between
 * pubs). Your check-in loan (the bonuses paid up front when you check in)
 * becomes yours once you've done that after your first check-in. Leave
 * sooner and it's repaid in full, even if that takes you into the red. To
 * place in the last-out rankings you must have done it before your last
 * check-out. Measuring a stretch rather than the whole night stops anyone
 * popping in early (or late) for a moment and getting the credit.
 */
export const STICK_AROUND_HOURS = 2;
export const STICK_AROUND_WINDOW_HOURS = 2.5;

/**
 * A week only counts towards your streak (and dodges the missed-week fine)
 * with at least this many minutes of Thursday time, so a five-minute visit
 * can't keep a streak alive.
 */
export const STREAK_MIN_WEEK_MINUTES = 10;

/** Line items with this id are loan repayments, which may take a balance below zero. */
export const LOAN_REPAYMENT_ID = "check-in-loan-repaid";

/**
 * Turning up early: lent to you the moment you make your first check-in of
 * the night, so it can be spent while you're out (see STICK_AROUND_HOURS). Worth `maxCoin` at or before
 * `fullFromHour`, falling steadily to nothing at `zeroAtHour` (London time).
 */
export const EARLY_CHECK_IN_BONUS = {
  maxCoin: 100,
  fullFromHour: 15,
  zeroAtHour: 19,
} as const;

/**
 * First in: ranked against everyone else who's already checked in tonight,
 * and lent the moment you check in, like the early check-in bonus.
 */
export const FIRST_IN_BONUSES = [40, 25, 10] as const;

/**
 * Last out: a prize split evenly between everyone who left within
 * `windowMinutes` of the last person out that night, since a group usually
 * leaves together and it shouldn't be a race to tap out last. It can only be
 * paid the morning after, once the night is settled (see settleNight). Only
 * people who stuck around before leaving (see STICK_AROUND_HOURS) count, and
 * it needs at least two of them out, so nobody wins it on their own.
 */
export const LAST_OUT_PRIZE = {
  coin: 200,
  windowMinutes: 15,
} as const;

/**
 * Staying the course: paid when your total pub time for the night (across
 * every pub you've checked into, so pub crawls aren't penalised) passes each
 * mark.
 */
export const DURATION_MILESTONES = [
  { hours: 1, coin: 20 },
  { hours: 2, coin: 30 },
  { hours: 3, coin: 30 },
  { hours: 4, coin: 40 },
  { hours: 5, coin: 30 },
  { hours: 6, coin: 30 },
] as const;

/**
 * Going home early: a night out that started in the evening (from
 * `eveningFromHour`), totalled less than `minHours` and was over before
 * `leftBeforeHour`.
 */
/** The line item an early-bath fine is announced with (see CoinEvent). */
export const EARLY_BATH_ID = "early-bath";

export const EARLY_BATH_FINE = {
  coin: 25,
  eveningFromHour: 16,
  leftBeforeHour: 22,
  minHours: 2,
} as const;

/**
 * Missing a week after you'd been coming: on top of losing the streak
 * itself. Grows with the streak you just threw away.
 */
export const MISSED_WEEK_FINE = {
  base: 25,
  perStreakWeek: 5,
  max: 150,
} as const;

/**
 * Sick Notes get dearer the more you hand in: each one costs `growth` times
 * the last one you handed in that calendar year (the first is the item's
 * price), so dodging the odd week is cheap but making a habit of it isn't.
 */
export const SICK_NOTE = {
  growth: 2,
} as const;

/**
 * What a Sick Note for the week `weekKey` (see getWeekKey) costs someone
 * whose notes so far are `excusedWeeks`: `basePrice` doubled (see
 * SICK_NOTE) for every one they've already handed in that year.
 */
export function getSickNotePrice(
  basePrice: number,
  weekKey: string,
  excusedWeeks: Record<string, boolean> = {},
) {
  const year = weekKey.slice(0, 5);
  const used = Object.entries(excusedWeeks).filter(
    ([key, excused]) => excused && key.startsWith(year),
  ).length;

  return basePrice * SICK_NOTE.growth ** used;
}

/**
 * The Jackpot Lotto pot. Every losing ticket goes in whole, and so does a cut
 * of the coin everyone else loses: `fineShare` of every fine (early baths,
 * missed weeks) and `lostBetShare` of the stake on every losing bet in the
 * casino. Those coins were leaving the economy anyway, so the pot grows with
 * how much everyone's playing (and misbehaving) without minting anything.
 */
export const LOTTO_FEED = {
  fineShare: 0.25,
  lostBetShare: 0.2,
} as const;

/** How much of `lost` coins goes into the lotto pot at `share`. Always a whole coin or none. */
export function getLottoFeed(lost: number, share: number) {
  return lost > 0 ? Math.max(Math.round(lost * share), 1) : 0;
}

/**
 * The Tontine (see tontine.ts): how many weeks it runs once enrolment closes,
 * and how many bros it needs to go ahead. Its stake is the shop item's price.
 * It's zero-sum: the pot is only the stakes, so it moves coin between players
 * rather than minting any.
 */
export const TONTINE = {
  weeks: 12,
  minMembers: 2,
} as const;

/**
 * The weekly puzzles (see puzzles.ts). Like the Tontine they're zero-sum:
 * the pot is only the entry fees, split between everyone who solves it. A
 * week nobody solves rolls over to the next, less the cut for whoever's behind
 * the bar, which goes in the lotto pot so a long run of rollovers can't grow
 * forever.
 */
export const WEEKLY_PUZZLE = {
  barCut: 0.1,
} as const;

/**
 * Cock in Hole for money (see cock-in-hole.ts): you stake between `minStake`
 * and `maxStake` on a game against BroBot, and beating him pays back your
 * stake times `payout`, more the soberer he is. Lose (or walk away) and the
 * stake's gone, with LOTTO_FEED.lostBetShare of it going in the lotto pot
 * like any other lost bet.
 */
export const COCK_IN_HOLE = {
  minStake: 5,
  maxStake: 100,
  stakes: [5, 10, 25, 50, 100],
  payout: { steaming: 2, merry: 3, sober: 4 },
} as const;

/** What winning a game of Cock in Hole staked at `stake` pays out, stake included. */
export function getCockInHolePayout(stake: number, difficulty: keyof typeof COCK_IN_HOLE.payout) {
  return Math.floor(stake * COCK_IN_HOLE.payout[difficulty]);
}

export function getWeekKey(onDate: Date) {
  // Time ledger week key format: T + 4-digit year + W + 2-digit ISO week (e.g. T2026W02)
  return format(onDate, "'T'yyyy'W'II");
}

/** Whether `times` has enough pub time in the week `key` for it to count. */
export function attendedWeek(times: Record<string, number> = {}, key: string) {
  return (times[key] ?? 0) >= STREAK_MIN_WEEK_MINUTES * MINUTE_MS;
}

/**
 * Consecutive weeks attended before `onDate`'s week. A week covered by a
 * Sick Note (see `excusedWeeks`) neither counts towards the streak nor breaks
 * it.
 */
export function getStreakWeeks(
  times: Record<string, number> = {},
  onDate: Date,
  excusedWeeks: Record<string, boolean> = {},
) {
  let streak = 0;

  for (let weekOffset = 1; weekOffset <= MAX_STREAK_WEEKS; weekOffset++) {
    const key = getWeekKey(subWeeks(onDate, weekOffset));

    if (attendedWeek(times, key)) {
      streak++;
    } else if (!excusedWeeks[key]) {
      return streak;
    }
  }

  return streak;
}

/**
 * The streak to show people: the weeks in a row before `onDate`'s week
 * (what the streak multiplier is worked out from, see getStreakWeeks), plus
 * this week once it's attended, or as soon as you've checked in on a
 * Thursday night (`outTonight`, see isOutTonight) rather than only once
 * that time's banked at checkout.
 */
export function getCurrentStreak(
  times: Record<string, number> = {},
  onDate: Date,
  excusedWeeks: Record<string, boolean> = {},
  outTonight = false,
) {
  const thisWeek = outTonight || attendedWeek(times, getWeekKey(onDate));

  return getStreakWeeks(times, onDate, excusedWeeks) + (thisWeek ? 1 : 0);
}

/**
 * Whether you've checked in tonight, it being a Thursday night: from your
 * first check-in to the end of the night, whether you're still in or have
 * checked out to move on, so your streak doesn't change between pubs.
 */
export function isOutTonight(lastCheckIn: Date | null | undefined, now: Date) {
  return !!lastCheckIn && isThursdayNight(now) && getNightKey(lastCheckIn) === getNightKey(now);
}

/**
 * The streak a session's streak multiplier stands for, counting tonight:
 * the inverse of getStreakMultiplier, plus one. Only exact below the cap,
 * and null for a multiplier that isn't a whole number of weeks (one stored
 * before the streak paid STREAK_MULTIPLIER_PER_WEEK a week).
 */
export function getStreakFromMultiplier(multiplier: number) {
  const weeks = (Math.min(multiplier, MAX_STREAK_MULTIPLIER) - 1) / STREAK_MULTIPLIER_PER_WEEK;

  if (multiplier >= MAX_STREAK_MULTIPLIER) {
    return Math.ceil(weeks) + 1;
  }

  return Number.isInteger(weeks) ? weeks + 1 : null;
}

export function getStreakMultiplier(streakWeeks: number) {
  const weeks = Math.max(streakWeeks, 0);

  return Math.min(1 + STREAK_MULTIPLIER_PER_WEEK * weeks, MAX_STREAK_MULTIPLIER);
}

/**
 * Decided by what the venue is rather than its `coinsPerHour`, which older
 * venues carry from before it counted, at whatever value they were given.
 */
export function getVenueMultiplier(venue: VenueLike | undefined) {
  if (venue?.id === SUPER_BONUS_PUB_ID) {
    return MAX_VENUE_MULTIPLIER;
  }

  return venue?.bonus === true ? BONUS_PUB_ACUMULATOR : 1;
}

interface VenueLike {
  id?: string;
  bonus?: boolean;
}

/** The hourly rate stored on a new session. */
export function getCoinRate(streakMultiplier: number, venue?: VenueLike) {
  return Math.round(BASE_COINS_PER_HOUR * streakMultiplier * getVenueMultiplier(venue));
}

/** The 6am London time that started the night `date` falls in. */
export function getNightStart(date: Date) {
  const zoned = toZonedTime(date, TIMEZONE);
  const day = zoned.getHours() < NIGHT_ROLLOVER_HOUR ? addDays(zoned, -1) : zoned;

  return fromZonedTime(
    new Date(day.getFullYear(), day.getMonth(), day.getDate(), NIGHT_ROLLOVER_HOUR),
    TIMEZONE,
  );
}

/** A key identifying the night `date` falls in, e.g. "2026-10-01" for a Thursday night. */
export function getNightKey(date: Date) {
  return format(toZonedTime(getNightStart(date), TIMEZONE), "yyyy-MM-dd");
}

/** Hours since midnight on the London clock, with the small hours counting on from 24. */
function getNightHour(date: Date) {
  const zoned = toZonedTime(date, TIMEZONE);
  const hours = zoned.getHours() + zoned.getMinutes() / 60;

  return zoned.getHours() < NIGHT_ROLLOVER_HOUR ? hours + 24 : hours;
}

/** "11pm", "12am", "1am"; hours past midnight count on from 24. */
export function formatHour(hour: number) {
  const clock = hour % 24;
  const suffix = clock < 12 ? "am" : "pm";

  return `${clock % 12 || 12}${suffix}`;
}

/**
 * The fields a session needs for its bonuses: when it ran, and where it sits
 * in the night: when the night's first check-in was, and how much pub time
 * was already banked tonight before this session started. Sessions without
 * `nightStart` (from before these bonuses existed, or added by an admin) earn
 * no bonuses.
 */
export interface NightSession {
  startTime: { toDate(): Date };
  endTime: { toDate(): Date } | null;
  nightStart?: { toDate(): Date } | null;
  nightTimeBefore?: number | null;
  /** False for a check-in that isn't a Thursday check-in. See Session.thursday. */
  thursday?: boolean | null;
}

/**
 * Whether a session earns the full rate and coin bonuses: only Thursday
 * check-ins do (see Session.thursday). One that's still going on a Thursday
 * night, or from before the rule, gets the benefit of the doubt.
 */
export function isThursdayCheckIn(session: { thursday?: boolean | null }) {
  return session.thursday !== false;
}

/**
 * The bonuses a session has earned by `now` (or by its end, once it's over).
 * Each bonus belongs to exactly one session: whichever one was running when
 * it was earned, so checking out and back in can't earn anything twice.
 */
export function getSessionBonuses(session: NightSession, now: Date = new Date()): CoinLineItem[] {
  if (!session.nightStart || !isThursdayCheckIn(session)) {
    return [];
  }

  const start = session.startTime.toDate();
  const end = session.endTime?.toDate() ?? now;
  const before = session.nightTimeBefore ?? 0;
  const after = before + Math.max(differenceInMilliseconds(end, start), 0);

  function crossed(markMs: number) {
    return before < markMs && markMs <= after;
  }

  const bonuses: CoinLineItem[] = [];

  for (const milestone of DURATION_MILESTONES) {
    if (crossed(milestone.hours * HOUR_MS)) {
      bonuses.push({
        id: `duration-${milestone.hours}h`,
        label: milestoneLabel(milestone.hours),
        coin: milestone.coin,
      });
    }
  }

  return bonuses;
}

function milestoneLabel(hours: number) {
  return `${hours} hour${hours === 1 ? "" : "s"} out`;
}

export function sumCoin(items: CoinLineItem[]) {
  return items.reduce((total, item) => total + item.coin, 0);
}

export interface UpcomingBonus extends CoinLineItem {
  at: Date;
}

/**
 * The bonuses a live session will earn next if the player stays put, soonest
 * first, for nudging people to stay for one more.
 */
export function getUpcomingBonuses(session: NightSession, now: Date = new Date()): UpcomingBonus[] {
  if (!session.nightStart || session.endTime || !isThursdayCheckIn(session)) {
    return [];
  }

  const nightTimeSoFar =
    (session.nightTimeBefore ?? 0) +
    Math.max(differenceInMilliseconds(now, session.startTime.toDate()), 0);
  const upcoming: UpcomingBonus[] = [];

  function inFromNow(markMs: number) {
    return new Date(now.getTime() + markMs - nightTimeSoFar);
  }

  for (const milestone of DURATION_MILESTONES) {
    const markMs = milestone.hours * HOUR_MS;

    if (nightTimeSoFar < markMs) {
      upcoming.push({
        id: `duration-${milestone.hours}h`,
        label: milestoneLabel(milestone.hours),
        coin: milestone.coin,
        at: inFromNow(markMs),
      });
    }
  }

  return upcoming.sort((a, b) => a.at.getTime() - b.at.getTime());
}

interface FinishedSession {
  startTime: { toDate(): Date };
  endTime: { toDate(): Date } | null;
  thursday?: boolean | null;
}

/**
 * One player's night: first check-in, last check-out, total pub time, and how
 * much of the STICK_AROUND_WINDOW_HOURS before their last check-out they were
 * actually checked in for.
 */
export interface NightSummary {
  profileId: string;
  firstIn: Date;
  lastOut: Date;
  totalMs: number;
  closingMs: number;
}

/** Total time `sessions` were checked in between `from` and `to`. */
export function pubTimeBetween(sessions: FinishedSession[], from: Date, to: Date) {
  return sessions.reduce((total, session) => {
    if (!session.endTime) return total;

    const start = Math.max(session.startTime.toDate().getTime(), from.getTime());
    const end = Math.min(session.endTime.toDate().getTime(), to.getTime());

    return total + Math.max(end - start, 0);
  }, 0);
}

/** Sums up a player's checked-out sessions from one night, or null if there are none. */
export function summariseNight(
  profileId: string,
  sessions: FinishedSession[],
): NightSummary | null {
  const finished = sessions.filter((session) => session.endTime);

  if (finished.length === 0) {
    return null;
  }

  const lastOut = new Date(Math.max(...finished.map((s) => s.endTime!.toDate().getTime())));

  return {
    profileId,
    firstIn: new Date(Math.min(...finished.map((s) => s.startTime.toDate().getTime()))),
    lastOut,
    totalMs: finished.reduce(
      (total, s) => total + differenceInMilliseconds(s.endTime!.toDate(), s.startTime.toDate()),
      0,
    ),
    closingMs: pubTimeBetween(
      finished,
      new Date(lastOut.getTime() - STICK_AROUND_WINDOW_HOURS * HOUR_MS),
      lastOut,
    ),
  };
}

function ordinal(place: number) {
  return ["1st", "2nd", "3rd"][place] ?? `${place + 1}th`;
}

/** The early check-in bonus for a first check-in of the night at `checkIn`. */
export function getEarlyCheckInCoin(checkIn: Date) {
  const { maxCoin, fullFromHour, zeroAtHour } = EARLY_CHECK_IN_BONUS;
  const hour = getNightHour(checkIn);
  const share = (zeroAtHour - hour) / (zeroAtHour - fullFromHour);

  // Rounded to the nearest 5 so it reads nicely.
  return Math.round((maxCoin * Math.min(Math.max(share, 0), 1)) / 5) * 5;
}

/**
 * The bonuses paid the moment a player makes their first check-in of the
 * night: one for the time, and one for how many people beat them to it
 * (`alreadyIn`: others who've already checked in tonight).
 */
export function getCheckInBonuses(checkIn: Date, alreadyIn: number): CoinLineItem[] {
  const bonuses: CoinLineItem[] = [];
  const early = getEarlyCheckInCoin(checkIn);

  if (early > 0) {
    bonuses.push({ id: "early-check-in", label: "Early check-in", coin: early });
  }

  const firstIn = FIRST_IN_BONUSES[alreadyIn];

  if (firstIn) {
    bonuses.push({
      id: `first-in-${alreadyIn + 1}`,
      label: `First in (${ordinal(alreadyIn)})`,
      coin: firstIn,
    });
  }

  return bonuses;
}

/**
 * Repays the check-in loan (the bonuses paid on checking in) if the player
 * didn't stick around for STICK_AROUND_HOURS, so a quick check-in can't farm
 * them, or if none of the night's check-ins turned out to be Thursday
 * check-ins. Repaid in full, even if it takes the balance below zero.
 */
export function getCheckInLoanRepayment(
  sessions: FinishedSession[],
  paid: CoinLineItem[],
): CoinLineItem | null {
  const total = sumCoin(paid);
  const night = summariseNight("", sessions);

  if (total <= 0) {
    return null;
  }

  // Check-in bonuses are for Thursday check-ins only.
  if (sessions.length > 0 && !sessions.some(isThursdayCheckIn)) {
    return {
      id: LOAN_REPAYMENT_ID,
      label: "Check-in loan repaid: no Thursday session with two bros",
      coin: -total,
    };
  }

  if (night) {
    const windowEnd = new Date(night.firstIn.getTime() + STICK_AROUND_WINDOW_HOURS * HOUR_MS);

    if (pubTimeBetween(sessions, night.firstIn, windowEnd) >= STICK_AROUND_HOURS * HOUR_MS) {
      return null;
    }
  }

  return {
    id: LOAN_REPAYMENT_ID,
    label: `Check-in loan repaid: didn't stay ${STICK_AROUND_HOURS} hours`,
    coin: -total,
  };
}

/**
 * The last-out prize for everyone out on the same night, keyed by profile id:
 * split between everyone who left within LAST_OUT_PRIZE.windowMinutes of the
 * last one out, rounded down so it never pays out more than the prize.
 */
export function getLastOutBonuses(nights: NightSummary[]): Map<string, CoinLineItem[]> {
  const ranked = nights.filter((night) => night.closingMs >= STICK_AROUND_HOURS * HOUR_MS);
  const bonuses = new Map<string, CoinLineItem[]>();

  if (ranked.length < 2) {
    return bonuses;
  }

  const last = Math.max(...ranked.map((night) => night.lastOut.getTime()));
  const winners = ranked.filter(
    (night) => night.lastOut.getTime() >= last - LAST_OUT_PRIZE.windowMinutes * 60 * 1000,
  );
  const coin = Math.max(Math.floor(LAST_OUT_PRIZE.coin / winners.length), 1);
  const label = winners.length === 1 ? "Last out" : `Last out (split ${winners.length} ways)`;

  for (const night of winners) {
    bonuses.set(night.profileId, [{ id: "last-out", label, coin }]);
  }

  return bonuses;
}

/**
 * The fine for a night's sessions (all from the same night, all checked out),
 * if the player went home early on a Thursday night. Any other night out is
 * never fined.
 */
export function getEarlyBathFine(sessions: FinishedSession[]): CoinLineItem | null {
  const night = summariseNight("", sessions);

  if (!night || !isThursdayNight(night.firstIn)) {
    return null;
  }

  const isEveningOut = getNightHour(night.firstIn) >= EARLY_BATH_FINE.eveningFromHour;
  const leftEarly = getNightHour(night.lastOut) < EARLY_BATH_FINE.leftBeforeHour;
  const tooShort = night.totalMs < EARLY_BATH_FINE.minHours * HOUR_MS;

  if (!isEveningOut || !leftEarly || !tooShort) {
    return null;
  }

  return {
    id: EARLY_BATH_ID,
    label: `Early bath: home before ${formatHour(EARLY_BATH_FINE.leftBeforeHour)}`,
    coin: -EARLY_BATH_FINE.coin,
  };
}

/**
 * The fine for skipping the week starting `weekStart`, if the player had a
 * streak going into it and no Sick Note to cover it. Never more than they
 * have.
 */
export function getMissedWeekFine(
  profile: {
    times?: Record<string, number>;
    excusedWeeks?: Record<string, boolean>;
    coin: number;
  },
  weekStart: Date,
): CoinLineItem | null {
  const week = startOfISOWeek(weekStart);
  const key = getWeekKey(week);
  const times = profile.times ?? {};
  const excusedWeeks = profile.excusedWeeks ?? {};

  if (attendedWeek(times, key) || excusedWeeks[key]) {
    return null;
  }

  const streakLost = getStreakWeeks(times, week, excusedWeeks);

  if (streakLost === 0) {
    return null;
  }

  const fine = Math.min(
    MISSED_WEEK_FINE.base + MISSED_WEEK_FINE.perStreakWeek * streakLost,
    MISSED_WEEK_FINE.max,
    Math.max(profile.coin, 0),
  );

  if (fine <= 0) {
    return null;
  }

  return {
    id: "missed-week",
    label: times[key]
      ? `Under ${STREAK_MIN_WEEK_MINUTES}m of Thursday time: lost a ${streakLost}-week streak`
      : `Missed a week: lost a ${streakLost}-week streak`,
    coin: -fine,
  };
}

/**
 * Power-ups (see powerups.ts). None of them is a sure thing: each one pays
 * off only if the night goes a certain way, and costs you if it doesn't, so
 * whether one's worth it depends on how long you'll stay, where you'll go and
 * who'll turn up. The ones that change your hourly rate add their extra on
 * top of it (×1.5 adds 0.5, ×0.5 takes off half) and stack by adding those
 * extras together, between ×0 and MAX_POWERUP_MULTIPLIER.
 */
export const MAX_POWERUP_MULTIPLIER = 5;

/**
 * Happy Hour: bought for the next `hours` of your check-in, and only worth
 * anything with company. Paid (or charged) the morning after, once everyone's
 * gone home: your hourly coin for that hour at the base rate, multiplied by
 * `byCompany[n]` less the ×1 you'd have earned anyway, where n is how many
 * other bros were in the same pub for at least `minMinutes` of it (the last
 * entry covers more). Drinking alone loses you the hour's coin.
 */
export const HAPPY_HOUR = {
  hours: 1,
  minMinutes: 20,
  byCompany: [0, 1, 2, 3, 4, 5],
} as const;

/**
 * Snowball: starts at `startMultiplier` (less than ×1, so it costs you at
 * first) and grows by `stepMultiplier` every hour you stay checked in to the
 * same pub, up to `maxMultiplier`. It melts when you check out, so it only
 * pays if you settle in: it's broken even by the end of the second hour.
 */
export const SNOWBALL = {
  startMultiplier: 0.75,
  stepMultiplier: 0.5,
  maxMultiplier: 3.5,
} as const;

/**
 * Pub Crawl: covers every check-in from when it's bought until the night's
 * over, paid (or charged) the morning after. Only what happens after buying
 * it counts, so it can't be bought at the end of a crawl to cash in on the
 * pubs already done: every pub you spend at least `minMinutes` in after
 * buying it (the one you're in included, from then on) counts; your hourly
 * coin since buying it, at the base rate, is multiplied by `byPubs[n - 1]` less the ×1 you'd have
 * earned anyway (the last entry covers more). Stay put in one pub and it
 * halves what you made; it takes three to come out ahead.
 */
export const PUB_CRAWL = {
  minMinutes: 30,
  byPubs: [0.5, 1, 1.5, 2, 2.5, 3],
} as const;

/*
 * The other night-long power-ups work like Pub Crawl: they cover every
 * check-in from when they're bought until the night's over, and are paid (or
 * charged) the morning after as your hourly coin since buying, at the base
 * rate, times the multiplier you ended up on, less the ×1 you'd have earned
 * anyway. The last entry of each table covers anything beyond it.
 */

/**
 * Last Orders: a bet on being the last one standing. Your place is how many
 * other people were still out more than LAST_OUT_PRIZE.windowMinutes after
 * your last check-out, plus one (so leaving with the last group counts as
 * last out), and pays
 * `byPlace[place - 1]`. You have to have stuck around (STICK_AROUND_HOURS)
 * before leaving to place at all, and it's a wash unless at least `minRivals`
 * other people were out that night, so a quiet Tuesday on your own can't win.
 */
export const LAST_ORDERS = {
  minRivals: 3,
  byPlace: [3, 1.5, 1, 0.5],
} as const;

/**
 * The Whip: a kitty that grows with every bro you drink with, in any pub. A
 * bro counts once they've shared pubs with you for `minMinutes` since you
 * bought it, and you're on `byBros[n]`: no-one turns up and you lose the lot.
 */
export const THE_WHIP = {
  minMinutes: 30,
  byBros: [0, 0.5, 1, 1.5, 2, 2.5, 3],
} as const;

/**
 * Pub Golf: pubs and company at once. A pub only counts as a hole if you
 * spent `minMinutes` there since buying it with at least one other bro in for
 * `minMinutes` of that too, and you're on `byHoles[n]`. Hardest to pull off,
 * so it pays the most.
 */
export const PUB_GOLF = {
  minMinutes: 30,
  byHoles: [0, 0.5, 1, 2, 3, 3.5],
} as const;

/**
 * However many night-long power-ups you stack, together they can add at most
 * this many times your hourly coin since the first of them was bought.
 */
export const MAX_NIGHT_POWERUP_EXTRA = 4;

/**
 * Mystery Pint: rolled once when it's drunk and kept for the rest of the
 * check-in, so the longer you stay the more it swings either way. Worth a
 * little over ×1.3 on average, and backfires more than a third of the time.
 */
export const MYSTERY_PINT_ODDS = [
  { multiplier: 0.5, chance: 0.35 },
  { multiplier: 1, chance: 0.2 },
  { multiplier: 1.5, chance: 0.2 },
  { multiplier: 2, chance: 0.15 },
  { multiplier: 3, chance: 0.07 },
  { multiplier: 5, chance: 0.03 },
] as const;

/** Padlock: how long Tortuga and the chaos items can't touch your coins. */
export const PADLOCK_HOURS = 12;
